import {
  Accordion,
  AccordionSummary,
  Avatar,
  Box,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  ListItem,
  ListItemIcon,
  TextField,
  Typography,
} from '@mui/material';
import { DICTIONARY } from 'Enum';
import { Dictionary } from 'Dictionary';
import {
  PlusBtn,
  MinusBtn,
  AccordionArrowBlue,
  RedAlert,
  PlusPurple,
  MinusPurple,
  AccordionArrowPurple,
} from 'Images';
import { useIsDesktop } from 'Hooks';
import { useState } from 'react';
import { IEquipmentItem } from 'API';
import { AccordionExpandIconBlue } from 'Components';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { IEquipmentOrderItem } from '../RowProduct';

interface IRowProductDisabled {
  item: IEquipmentItem;
  index: number;
}

export const RowProductDisabled = ({ item, index }: IRowProductDisabled) => {
  const isDesktop = useIsDesktop();
  const dictionary = Dictionary[DICTIONARY.Equipment].createOrder;

  const [equipmentItem] = useState<IEquipmentOrderItem>({
    itemNote: '',
    quantity: item.maxAVGQuantityPerMonth,
    quantityNote: '',
  });

  return (
    <>
      <ListItem sx={{ p: 0, mb: 2 }}>
        {isDesktop ? (
          <Accordion
            disabled
            tabIndex={0}
            role='group'
            aria-label={item.itemName}
            onKeyDown={(e) => onEnterOrEscapeKeyDown(e)}
            sx={{
              width: '100%',
              boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
              border: '1px solid',
              borderColor: 'primary.shade_200',

              '&:first-of-type': {
                borderRadius: '16px',
              },
              '&.Mui-disabled': {
                backgroundColor: '#fff',
              },
            }}
            disableGutters
          >
            <AccordionSummary
              sx={{
                pt: 3,
                pb: '21px',
                pr: 3,
                pl: 3,
                borderRadius: '16px',
                '& .MuiAccordionSummary-content': {
                  margin: '0',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: '#fff',
                },
                '&.Mui-disabled': {
                  opacity: 1,
                },
              }}
              expandIcon={
                <Avatar
                  sx={{
                    width: isDesktop ? '40px' : '23px',
                    height: isDesktop ? '40px' : '23px',
                    opacity: 0.38,
                  }}
                  src={AccordionArrowPurple}
                  alt=''
                />
              }
            >
              <Grid container>
                <Grid
                  container
                  item
                  xs={12}
                  flexWrap={'nowrap'}
                  sx={{
                    opacity: 0.38,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <Checkbox edge='start' tabIndex={-1} />
                  </ListItemIcon>
                  <Grid
                    container
                    item
                    sx={{ maxWidth: '300px', alignItems: 'center' }}
                  >
                    <Typography fontWeight={700}>{item.itemName}</Typography>
                  </Grid>
                  <Divider
                    orientation='vertical'
                    variant='middle'
                    flexItem
                    sx={{
                      m: '0 50px',
                      height: '48px',
                      alignSelf: 'center',
                      borderColor: '#EDF2FB',
                    }}
                    aria-hidden='true'
                  />
                  <Grid
                    item
                    container
                    justifyContent='flex-start'
                    alignItems='center'
                  >
                    <IconButton
                      tabIndex={-1}
                      sx={{
                        width: '40px',
                        height: '40px',
                      }}
                      aria-label={dictionary.increaseQuantity}
                    >
                      <Avatar
                        src={PlusPurple}
                        sx={{ width: '32px', height: '32px' }}
                        alt=''
                      />
                    </IconButton>
                    <TextField
                      disabled
                      name={`item-quantity-field-${index}`}
                      id={`item-quantity-field-${index}`}
                      value={equipmentItem.quantity}
                      label={dictionary.quantity}
                      variant='outlined'
                      type={'number'}
                      inputProps={{
                        style: { padding: '0 20px', height: '40px' },
                      }}
                      sx={{
                        maxWidth: '100px',
                        p: 0,
                        margin: '0 20px',
                        borderRadius: '6px',
                        '& .Mui-disabled': {
                          color: 'rgba(0, 0, 0, 1)',
                          WebkitTextFillColor: 'rgba(0, 0, 0, 1)',
                        },
                        '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                          borderRadius: '6px',
                          border: '1px solid rgba(23, 82, 175, 0.2)',
                        },
                      }}
                    />
                    <IconButton
                      tabIndex={-1}
                      sx={{
                        width: '40px',
                        height: '40px',
                      }}
                      aria-label={dictionary.decreaseQuantity}
                    >
                      <Avatar
                        src={MinusPurple}
                        sx={{ width: '32px', height: '32px' }}
                        alt=''
                      />
                    </IconButton>
                  </Grid>
                </Grid>
                <Grid container item xs={12}>
                  <Grid
                    item
                    container
                    alignItems='center'
                    gap={1}
                    flexWrap='nowrap'
                    sx={{
                      margin: '20px 0 3px',
                    }}
                  >
                    <Grid
                      item
                      sx={{
                        alignSelf: 'start',
                        position: 'relative',
                        top: '5px',
                      }}
                    >
                      <Avatar
                        alt={''}
                        src={RedAlert}
                        sx={{
                          width: '16px',
                          height: '16px',
                          alignSelf: 'center',
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography fontSize={'18px'}>
                        {dictionary.itemUnavailable}
                        <br />
                        {dictionary.contactDoctorForAlternativeItem}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </AccordionSummary>
          </Accordion>
        ) : (
          // MOBILE UI
          <Accordion
            disabled
            sx={{
              width: '100%',
              boxShadow: '0px 3px 21px rgba(45, 112, 217, 0.15)',
              '&:first-of-type': {
                borderRadius: '16px',
              },
              '&.Mui-disabled': {
                backgroundColor: '#fff',
              },
            }}
            disableGutters
          >
            <AccordionSummary
              expandIcon={<AccordionExpandIconBlue />}
              sx={{
                pt: 3,
                pb: '21px',
                pr: 3,
                pl: 3,
                borderRadius: '16px',
                '& .MuiAccordionSummary-content': {
                  margin: '0',
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  alignSelf: 'start',
                  mt: '-5px',
                  opacity: 0.38,
                },
                '&.Mui-disabled': {
                  opacity: 1,
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'inline-flex', opacity: 0.38 }}>
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <Checkbox edge='start' tabIndex={-1} />
                  </ListItemIcon>

                  <Grid
                    container
                    item
                    sx={{ maxWidth: '300px', alignItems: 'center' }}
                  >
                    <Typography fontWeight={700}>{item.itemName}</Typography>
                  </Grid>
                </Box>
                <Divider
                  aria-hidden='true'
                  orientation='horizontal'
                  variant='middle'
                  flexItem
                  sx={{
                    m: '10px',
                    height: '1px',
                    width: '115%',
                    alignSelf: 'center',
                    borderColor: '#EDF2FB',
                    mb: '24px',
                    opacity: 0.38,
                  }}
                />
                <Grid
                  container
                  direction='row'
                  justifyContent='flex-start'
                  alignItems='center'
                  sx={{
                    opacity: 0.38,
                  }}
                >
                  <IconButton
                    tabIndex={-1}
                    sx={{
                      border: '1px solid rgba(23, 82, 175, 0.2)',
                      borderRadius: '6px',
                      width: '40px',
                      height: '40px',
                    }}
                  >
                    <Avatar
                      src={PlusBtn}
                      sx={{ width: '20px', height: '20px' }}
                      alt=''
                    />
                  </IconButton>
                  <TextField
                    disabled
                    name={`item-quantity-field-${index}`}
                    id={`item-quantity-field-${index}`}
                    value={equipmentItem.quantity}
                    label={dictionary.quantity}
                    variant='outlined'
                    inputProps={{
                      style: { padding: '8.5px' },
                    }}
                    sx={{
                      maxWidth: '100px',
                      p: 0,
                      margin: '0 10px',
                      borderRadius: '6px',
                      '& .Mui-disabled': {
                        color: 'rgba(0, 0, 0, 1)',
                        WebkitTextFillColor: 'rgba(0, 0, 0, 1)',
                      },
                      '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '6px',
                        border: '1px solid rgba(23, 82, 175, 0.2)',
                      },
                    }}
                  />
                  <IconButton
                    tabIndex={-1}
                    sx={{
                      border: '1px solid rgba(23, 82, 175, 0.2)',
                      borderRadius: '6px',
                      width: '40px',
                      height: '40px',
                    }}
                  >
                    <Avatar
                      src={MinusBtn}
                      sx={{ width: '20px', height: '2px' }}
                      alt=''
                    />
                  </IconButton>
                </Grid>
                <Grid
                  item
                  container
                  alignItems='center'
                  gap={1}
                  flexWrap='nowrap'
                  sx={{
                    marginTop: '16px',
                  }}
                >
                  <Grid
                    item
                    sx={{
                      alignSelf: 'start',
                      position: 'relative',
                      top: '5px',
                    }}
                  >
                    <Avatar
                      alt={''}
                      src={RedAlert}
                      sx={{
                        width: '16px',
                        height: '16px',
                        alignSelf: 'center',
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Typography fontSize={'16px'}>
                      {dictionary.itemUnavailable}
                      <br />
                      {dictionary.contactDoctorForAlternativeItem}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </AccordionSummary>
          </Accordion>
        )}
      </ListItem>
    </>
  );
};
